import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";

function Media({ url, kind, className }) {
  if (!url) return null;
  if (kind === "video") {
    return (
      <video
        src={fileUrl(url)}
        className={className}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="publication-video"
      />
    );
  }
  return <img src={fileUrl(url)} alt="Publication ISMO-TECH" className={className} loading="eager" data-testid="publication-image" />;
}

function Frame({ item }) {
  const inner = (
    <>
      {/* Desktop / large screens */}
      <div className="relative hidden w-full overflow-hidden rounded-2xl lg:block" style={{ aspectRatio: "1128 / 150" }}>
        <Media url={item.desktop_url} kind={item.desktop_kind} className="absolute inset-0 h-full w-full object-cover" />
      </div>
      {/* Mobile & tablet */}
      <div className="relative block w-full overflow-hidden rounded-xl lg:hidden" style={{ aspectRatio: "375 / 120" }}>
        <Media url={item.mobile_url} kind={item.mobile_kind} className="absolute inset-0 h-full w-full object-cover" />
      </div>
    </>
  );

  if (item.link_url) {
    const external = item.link_url.startsWith("http");
    return (
      <a
        href={item.link_url}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className="block"
        data-testid="publication-link"
      >
        {inner}
      </a>
    );
  }
  return inner;
}

export default function PublicationsBanner() {
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get("/publications")
      .then(({ data }) => active && setData(data))
      .catch(() => active && setData({ enabled: false, items: [] }));
    return () => { active = false; };
  }, []);

  const items = useMemo(() => (data?.enabled ? data.items || [] : []), [data]);

  useEffect(() => {
    if (index >= items.length) setIndex(0);
  }, [items.length, index]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (items.length <= 1) return;
    const current = items[index] || items[0];
    const ms = Math.max(1, Number(current?.duration_seconds) || 6) * 1000;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % items.length);
    }, ms);
    return () => clearTimeout(timerRef.current);
  }, [index, items]);

  if (!items.length) return null;
  const current = items[index] || items[0];

  return (
    <section className="w-full bg-white" data-testid="publications-banner" aria-label="Nos publications">
      <div className="container-it py-2 sm:py-2.5">
        <div className="relative overflow-hidden rounded-2xl bg-[#0052FF] shadow-sm">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id || index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <Frame item={current} />
            </motion.div>
          </AnimatePresence>

          {items.length > 1 && (
            <div className="pointer-events-none absolute bottom-1.5 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid="publication-dots">
              {items.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-4 bg-white" : "w-1.5 bg-white/50"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
